/** px/ms: above this, the flick's direction decides, not the position. */
const FLICK = 0.5;

/**
 * Collapsible sheet: collapsed or open when the finger lets go. `offset` is how far the sheet was
 * pushed down (0 open, `max` collapsed), `velocity` in px/ms, positive downwards.
 */
export function snapCollapsed(offset: number, max: number, velocity: number): boolean {
  if (max <= 0) return false;
  if (Math.abs(velocity) > FLICK) return velocity > 0;
  return offset > max / 2;
}

/** px: below this the finger hasn't chosen a direction yet. */
const SLOP = 6;

/**
 * Who a drag on a collapsible sheet belongs to, decided once the finger moved enough: the sheet
 * itself (move it) or its content (native scroll, a horizontal slider). Like native sheets: pulling
 * down moves the sheet once its content is at the top; a collapsed or moving sheet takes every
 * vertical drag; pushing up an open sheet scrolls its content.
 */
export function dragTarget(s: {
  dx: number;
  dy: number;
  collapsed: boolean;
  offset: number;
  scrollTop: number;
}): "sheet" | "content" | undefined {
  if (Math.max(Math.abs(s.dx), Math.abs(s.dy)) < SLOP) return undefined;
  if (Math.abs(s.dx) > Math.abs(s.dy)) return "content";
  if (s.collapsed || s.offset > 0) return "sheet";
  return s.dy > 0 && s.scrollTop <= 0 ? "sheet" : "content";
}

/**
 * Mid-gesture hand-off: a vertical drag first given to the content goes to the sheet once the
 * content is back at its top and the finger still pulls down (`step`: px since the last move), so
 * one pull scrolls the content back and then collapses the sheet.
 */
export function takesOver(s: { horizontal: boolean; step: number; scrollTop: number }): boolean {
  return !s.horizontal && s.step > 0 && s.scrollTop <= 0;
}
